import { useParams } from 'react-router-dom';
import usePatientFieldsMap from './usePatientFieldsMap';
import { GeneralIds, ModelTypes } from 'types/types';
import useRequest from './useRequest';
import { FieldTemplate, FormTemplateType } from 'api/models';
import { getAvailablePatientFields, getProjectsFieldTemplates } from 'api';
import useQuery from './useQuery';
import { FormTemplateEditorMode } from 'routes/FormTemplateEditor/FormTemplateEditor';

// sets the recoil state for the condition data
export default function useConditionData() {
  const patientFieldMap = usePatientFieldsMap();
  const { projectId } = useParams<GeneralIds>();

  const { data: fields, loading: fieldTemplatesLoading } = useRequest<
    FieldTemplate[]
  >(getProjectsFieldTemplates, {}, [{ projectId }]);

  const { data: formTemplateTypes, loading: formTemplateTypesLoading } =
    useQuery<FormTemplateType[]>({
      type: ModelTypes.formTemplateType,
      ids: {
        projectId,
      },
    });

  const { data: sampleTemplateTypes, loading: sampleTemplateTypesLoading } =
    useQuery<FormTemplateType[]>({
      type: ModelTypes.formTemplateType,
      queryParams: {
        type: FormTemplateEditorMode.sample,
      },
      ids: {
        projectId,
      },
    });

  const { data: patientFieldsResponse, loading: patientFieldsLoading } =
    useRequest<string[]>(getAvailablePatientFields);
  const loading =
    fieldTemplatesLoading ||
    patientFieldsLoading ||
    formTemplateTypesLoading ||
    sampleTemplateTypesLoading;

  const patientFields =
    patientFieldsResponse?.map((patientField: string) => {
      if (!patientFieldMap.has(patientField)) {
        throw new Error(
          `Patient field ${patientField} not found in patientFieldMap, probably new fields were added in the API`,
        );
      }

      return patientFieldMap.get(patientField)!;
    }) ?? [];

  return {
    patientFields,
    fieldTemplates: fields,
    formTemplateTypes: [
      ...(formTemplateTypes ?? []),
      ...(sampleTemplateTypes ?? []),
    ],
    loading,
  };
}
